/**
 * Life4Earth V2 — Admin Workspace Cleanup (Problem Workspace hard-delete + restore)
 *
 * Lists EVERY problem workspace, including soft-deleted ones, and lets a sysadmin
 * permanently hard-delete one (with all dependent data) or restore a soft-deleted one.
 *
 *   GET    /api/ProblemWorkspaces/admin/all       (sysadmin; all, incl. trashed)
 *   DELETE /api/ProblemWorkspaces/{guid}/hard      (sysadmin; cascade)
 *   POST   /api/ProblemWorkspaces/{guid}/restore   (owner or sysadmin)
 *
 * English only (system admin UI per CLAUDE.md). Mirrors AdminContentCleanup.
 */

import React, { useState, useCallback } from 'react';
import {
  fetchAdminWorkspaces,
  hardDeleteWorkspace,
  restoreWorkspace,
  type AdminWorkspaceSummary,
} from '../../api/workspaces';
import styles from './AdminPages.module.css';

export default function AdminWorkspaceCleanup() {
  const [rows, setRows] = useState<AdminWorkspaceSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [confirmTarget, setConfirmTarget] = useState<AdminWorkspaceSummary | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [busyGuid, setBusyGuid] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const nameOf = (r: AdminWorkspaceSummary) => r.title || r.presentingConcern || '(untitled)';

  const handleLoad = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResultMsg(null);
    try {
      setRows(await fetchAdminWorkspaces());
      setSearched(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }, []);

  const filtered = rows.filter((r) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      nameOf(r).toLowerCase().includes(q) ||
      r.workspaceGuid.toLowerCase().includes(q) ||
      (r.communityDisplayName || '').toLowerCase().includes(q) ||
      (r.teamLeadDisplayName || '').toLowerCase().includes(q) ||
      r.phase.toLowerCase().includes(q)
    );
  });

  const liveCount = rows.filter((r) => !r.deletedAt).length;
  const trashCount = rows.filter((r) => r.deletedAt).length;

  const handleRestore = useCallback(async (r: AdminWorkspaceSummary) => {
    setBusyGuid(r.workspaceGuid);
    setError(null);
    setResultMsg(null);
    try {
      await restoreWorkspace(r.workspaceGuid);
      setRows((prev) => prev.map((x) => (x.workspaceGuid === r.workspaceGuid ? { ...x, deletedAt: null } : x)));
      setResultMsg(`"${nameOf(r)}" restored.`);
    } catch (err: any) {
      setError(err?.message || 'Restore failed');
    } finally {
      setBusyGuid(null);
    }
  }, []);

  const handleHardDelete = useCallback(async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    setError(null);
    setResultMsg(null);
    try {
      const res = await hardDeleteWorkspace(confirmTarget.workspaceGuid);
      setRows((prev) => prev.filter((x) => x.workspaceGuid !== confirmTarget.workspaceGuid));
      const removed = Object.entries(res.recordsRemoved)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
      setResultMsg(`${res.message}${removed ? ` — removed ${removed}` : ''}`);
      setConfirmTarget(null);
      setConfirmText('');
    } catch (err: any) {
      setError(err?.message || 'Hard delete failed');
    } finally {
      setDeleting(false);
    }
  }, [confirmTarget]);

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Workspace Cleanup</h1>
          <p className={styles.pageSubtitle}>
            Problem workspaces across all states (including trashed). Restore a soft-deleted
            workspace, or permanently hard-delete one with all dependent data (team, evidence,
            solutions, phases, AI sessions, and more). Hard delete is irreversible.
          </p>
        </div>
      </div>

      {error && <div className={`${styles.statusBar} ${styles.status_error}`}>{error}</div>}
      {resultMsg && <div className={`${styles.statusBar} ${styles.status_success}`}>{resultMsg}</div>}

      <div className={styles.section}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className={styles.input}
            type="text"
            placeholder="Filter by name, GUID, community, lead, or phase..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <button className={styles.btnPrimary} onClick={handleLoad} disabled={loading}>
            {loading ? 'Loading...' : searched ? 'Refresh' : 'Load Workspaces'}
          </button>
        </div>
        {searched && (
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, marginBottom: 0 }}>
            {filtered.length} of {rows.length} shown · {liveCount} live, {trashCount} in trash
          </p>
        )}
      </div>

      {searched && filtered.length === 0 && !loading && (
        <p className={styles.emptyMsg}>
          {rows.length === 0 ? 'No workspaces found.' : 'No workspaces match your filter.'}
        </p>
      )}

      {filtered.length > 0 && (
        <div className={styles.section} style={{ padding: 0 }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Workspace</th>
                <th style={thStyle}>Community</th>
                <th style={thStyle}>Lead</th>
                <th style={thStyle}>Phase</th>
                <th style={thStyle}>State</th>
                <th style={thStyle}>Created</th>
                <th style={{ ...thStyle, width: 150, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isTrashed = !!r.deletedAt;
                const busy = busyGuid === r.workspaceGuid;
                return (
                  <tr key={r.workspaceGuid} style={trStyle}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>{nameOf(r)}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{r.workspaceGuid}</div>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 13, color: '#4b5563' }}>{r.communityDisplayName || '—'}</td>
                    <td style={{ ...tdStyle, fontSize: 13, color: '#4b5563' }}>{r.teamLeadDisplayName || '—'}</td>
                    <td style={{ ...tdStyle, fontSize: 12, color: '#6b7280' }}>{r.phase}</td>
                    <td style={tdStyle}>
                      <span style={isTrashed ? trashStateStyle : liveStateStyle}>{isTrashed ? 'Trash' : 'Live'}</span>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: '#6b7280' }}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {isTrashed && (
                        <button onClick={() => handleRestore(r)} disabled={busy} style={restoreBtnStyle} title={`Restore "${nameOf(r)}"`}>
                          {busy ? '…' : 'Restore'}
                        </button>
                      )}
                      <button
                        onClick={() => { setConfirmTarget(r); setConfirmText(''); setError(null); setResultMsg(null); }}
                        style={deleteBtnStyle}
                        title={`Permanently delete "${nameOf(r)}"`}
                      >
                        &times;
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirmTarget && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#991b1b' }}>
              Permanently Delete Workspace
            </h3>
            <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5, margin: '0 0 12px' }}>
              This permanently deletes <strong>{nameOf(confirmTarget)}</strong> and ALL dependent data
              (team, constraints, phase history, evidence items + artifacts + tasks, clarifying questions,
              triage entries, AI sessions, complaint clusters, solution proposals, and causal explanations).
            </p>
            <p style={{ fontSize: 13, color: '#991b1b', fontWeight: 600, margin: '0 0 12px' }}>
              This action cannot be undone.
            </p>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              <div><strong>GUID:</strong> <code style={{ fontSize: 11 }}>{confirmTarget.workspaceGuid}</code></div>
              <div><strong>State:</strong> {confirmTarget.deletedAt ? 'In trash' : 'Live'}</div>
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
              Type the workspace name to confirm:
            </label>
            <input
              className={styles.input}
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={nameOf(confirmTarget)}
              autoFocus
              style={{ marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className={styles.btnSecondary} onClick={() => { setConfirmTarget(null); setConfirmText(''); }} disabled={deleting}>
                Cancel
              </button>
              <button
                onClick={handleHardDelete}
                disabled={confirmText.trim() !== nameOf(confirmTarget).trim() || deleting}
                style={{
                  ...hardDeleteBtnStyle,
                  opacity: confirmText.trim() !== nameOf(confirmTarget).trim() || deleting ? 0.4 : 1,
                  cursor: confirmText.trim() !== nameOf(confirmTarget).trim() || deleting ? 'default' : 'pointer',
                }}
              >
                {deleting ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inline styles (mirror AdminContentCleanup) ──────────────
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle: React.CSSProperties = {
  textAlign: 'start', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
};
const tdStyle: React.CSSProperties = { padding: '10px 16px', verticalAlign: 'middle' };
const trStyle: React.CSSProperties = { borderBottom: '1px solid #f3f4f6' };
const liveStateStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, color: '#166534', background: '#dcfce7',
};
const trashStateStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, color: '#92400e', background: '#fef3c7',
};
const restoreBtnStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, padding: '5px 10px', marginInlineEnd: 8, color: '#166534',
  background: 'rgba(22,101,52,0.06)', border: '1px solid rgba(22,101,52,0.2)', borderRadius: 4, cursor: 'pointer',
};
const deleteBtnStyle: React.CSSProperties = {
  width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
  color: '#ef4444', background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)', borderRadius: 4, cursor: 'pointer',
};
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const modalStyle: React.CSSProperties = {
  background: '#ffffff', borderRadius: 10, padding: '28px 32px', maxWidth: 480, width: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const hardDeleteBtnStyle: React.CSSProperties = {
  padding: '8px 20px', fontSize: 13, fontWeight: 600, color: '#ffffff', background: '#dc2626', border: 'none', borderRadius: 6,
};
